import type { GisLens, MandalAggregate } from '../api/types'

/**
 * The single documented **Attention Score** — the one ranking spine reused by the Overview
 * "Today's priorities", the Overview map, and the Issues map. Additive, count-based, fully
 * transparent (every point contributor is shown). It replaces the retired opaque composite in
 * `lib/risk.ts`. See docs/superpowers/specs/2026-06-24-gis-view-v2-port-design.md (decision D1).
 *
 *   score = critical×10 + overdueIssue×6 + openIssue×2 + overdueSurvey×3 + 0.5·covGap + 0.3·offGap
 *   gap   = max(0, 80 − pct)            (coverage / officer-completion shortfalls vs the 80% bar)
 */
export const ATTENTION_WEIGHTS = {
  critical: 10,
  overdueIssue: 6,
  openIssue: 2,
  overdueSurvey: 3,
  covGap: 0.5,
  offGap: 0.3,
} as const

export const ATTENTION_CAPTION =
  'Attention = critical×10 · overdue issue×6 · open×2 · overdue survey×3 · coverage/officer gap'

/** The attention bar (80%) below which coverage/officer completion start contributing a gap penalty. */
const GAP_BAR = 80
/** A mandal with fewer than this many open issue+survey signals is tagged "thin data" (jumpy small-N). */
const THIN_SIGNAL = 3

export type AttentionKey = 'critical' | 'overdueIssue' | 'openIssue' | 'overdueSurvey' | 'covGap' | 'offGap'

export interface AttentionPart {
  key: AttentionKey
  pts: number
  label: string
}

export interface AttentionScore {
  mandal: string
  score: number
  parts: AttentionPart[]
  critical: number
  overdueIssue: number
  openIssue: number
  overdueSurvey: number
  coverage: number | null
  officer: number | null
  /** Small-N guard: a real-but-tiny open signal (1–2) — render a "thin data" tag, don't over-trust the rank. */
  thin: boolean
}

const plural = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`

/**
 * Compute the attention score for one mandal, entirely from its bulk row. The open-issue terms come
 * from the server-computed `openCritical` / `openOverdue` / `openIssues` fields (backend request #2),
 * so the score is exact (no client issue-feed join / per-page cap) and the Overview priorities, the
 * Overview map, and the Issues map all read the same number.
 */
export function attention(m: MandalAggregate): AttentionScore {
  const critical = m.openCritical
  const overdueIssue = m.openOverdue
  const openIssue = Math.max(0, m.openIssues - m.openCritical - m.openOverdue) // non-critical, on-track
  const overdueSurvey = m.overdueSites
  const covGap = m.coverage === null ? 0 : Math.max(0, GAP_BAR - m.coverage)
  const offGap = m.officerAvgCompletion === null ? 0 : Math.max(0, GAP_BAR - m.officerAvgCompletion)

  const parts: AttentionPart[] = []
  let score = 0
  const add = (key: AttentionKey, n: number, weight: number, label: string): void => {
    if (n <= 0) {
      return
    }
    const pts = Math.round(n * weight)
    score += pts
    parts.push({ key, pts, label })
  }
  add('critical', critical, ATTENTION_WEIGHTS.critical, plural(critical, 'critical', 'critical'))
  add('overdueIssue', overdueIssue, ATTENTION_WEIGHTS.overdueIssue, plural(overdueIssue, 'overdue', 'overdue'))
  add('openIssue', openIssue, ATTENTION_WEIGHTS.openIssue, plural(openIssue, 'open issue', 'open issues'))
  add('overdueSurvey', overdueSurvey, ATTENTION_WEIGHTS.overdueSurvey, plural(overdueSurvey, 'overdue survey', 'overdue surveys'))
  add('covGap', covGap, ATTENTION_WEIGHTS.covGap, `coverage gap ${covGap}%`)
  add('offGap', offGap, ATTENTION_WEIGHTS.offGap, `officer gap ${offGap}%`)

  const signal = critical + overdueIssue + openIssue + overdueSurvey
  return {
    mandal: m.name,
    score,
    parts,
    critical,
    overdueIssue,
    openIssue,
    overdueSurvey,
    coverage: m.coverage,
    officer: m.officerAvgCompletion,
    thin: signal > 0 && signal < THIN_SIGNAL,
  }
}

/**
 * Mandals ranked by attention, highest first. Score-0 mandals (healthy / no data) drop out so an
 * empty mandal never tops the list. Ties break on mandal name — a stable order with no per-period jitter.
 */
export function attentionRanked(mandals: MandalAggregate[]): AttentionScore[] {
  return mandals
    .map((m) => attention(m))
    .filter((a) => a.score > 0)
    .sort((a, b) => b.score - a.score || a.mandal.localeCompare(b.mandal))
}

/** The biggest single point contributor — drives the row caption + the cross-lens routing. */
export function topPart(a: AttentionScore): AttentionPart | null {
  if (a.parts.length === 0) {
    return null
  }
  return [...a.parts].sort((x, y) => y.pts - x.pts)[0]!
}

/** Which lens fixes this mandal's dominant problem — for Overview "Today's priorities" routing. */
export function reasonLensOf(a: AttentionScore): GisLens {
  const top = topPart(a)
  if (top === null) {
    return 'issues'
  }
  if (top.key === 'offGap') {
    return 'officers'
  }
  if (top.key === 'covGap' || top.key === 'overdueSurvey') {
    return 'coverage'
  }
  return 'issues'
}

/** Fixed, decision-meaningful attention bands (stable across periods — never normalize-to-max). */
export const ATTENTION_BANDS: readonly { min: number; label: string; color: string }[] = [
  { min: 35, label: '35+', color: '#b01d12' },
  { min: 20, label: '20–34', color: '#e0552f' },
  { min: 10, label: '10–19', color: '#f2a35f' },
  { min: 1, label: '1–9', color: '#f8d7b0' },
  { min: 0, label: '0', color: '#eef2f8' },
]

/** Choropleth fill for an attention score, by fixed band. */
export function attentionColor(score: number): string {
  return (ATTENTION_BANDS.find((b) => score >= b.min) ?? ATTENTION_BANDS[ATTENTION_BANDS.length - 1]!).color
}
